import React, { useState, useEffect } from 'react';

/**
 * MobileLoadingSplash - Premium Loading Animation
 * 
 * A beautiful full-screen loading splash with logo animation,
 * progress indicator, and smooth transitions.
 * 
 * Usage:
 *   {loading && <MobileLoadingSplash />}
 * 
 * Props:
 *   - logoSrc: path to logo image (default: '/e.png')
 *   - storeName: optional store name to display (changes per user/store)
 *   - onComplete: callback when animation finishes (for exit animation)
 */
const MobileLoadingSplash = ({ 
  logoSrc = '/e.png', 
  storeName = null,
  onComplete = null 
}) => {
  const [progress, setProgress] = useState(0);
  const [dots, setDots] = useState('');

  // Animate progress bar
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev; // Cap at 90% until actually loaded
        return prev + Math.random() * 15;
      });
    }, 200);
    return () => clearInterval(interval);
  }, []);

  // Animate loading dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating orbs - using sky blue to match logo */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-sky-400/10 rounded-full blur-3xl animate-float-medium" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-sky-500/5 rounded-full blur-3xl animate-pulse-slow" />
        
        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center px-8">
        {/* Logo container with glow effect */}
        <div className="relative mb-8">
          {/* Outer glow rings - sky blue to match logo */}
          <div className="absolute inset-0 -m-8">
            <div className="absolute inset-0 bg-sky-500/20 rounded-full blur-2xl animate-pulse-slow" />
          </div>
          <div className="absolute inset-0 -m-4">
            <div className="absolute inset-0 bg-sky-400/30 rounded-full blur-xl animate-pulse" />
          </div>
          
          {/* Spinning ring - sky blue gradient */}
          <div className="absolute -inset-4">
            <svg className="w-full h-full animate-spin-slow" viewBox="0 0 100 100">
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#0ea5e9" stopOpacity="1" />
                  <stop offset="50%" stopColor="#38bdf8" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
                </linearGradient>
              </defs>
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="url(#gradient)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="70 200"
              />
            </svg>
          </div>

          {/* Logo image */}
          <div className="relative w-24 h-24 rounded-2xl overflow-hidden shadow-2xl shadow-sky-500/25 animate-logo-float">
            <img 
              src={logoSrc} 
              alt="Logo" 
              className="w-full h-full object-cover"
            />
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent animate-shine" />
          </div>
        </div>

        {/* Brand text */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-1 animate-fade-in-up">
            Exitium
          </h1>
          {storeName && (
            <p className="text-sky-400 text-sm font-medium animate-fade-in-up-delay">
              {storeName}
            </p>
          )}
        </div>

        {/* Progress section */}
        <div className="w-64 space-y-3 animate-fade-in-up-delay-2">
          {/* Progress bar container */}
          <div className="relative h-1.5 bg-slate-700/50 rounded-full overflow-hidden backdrop-blur-sm">
            {/* Animated background */}
            <div className="absolute inset-0 bg-gradient-to-r from-slate-700/0 via-slate-600/30 to-slate-700/0 animate-shimmer" />
            
            {/* Progress fill - sky blue gradient */}
            <div 
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-sky-500 via-sky-400 to-sky-500 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${Math.min(progress, 100)}%` }}
            >
              {/* Glow on progress bar */}
              <div className="absolute inset-0 bg-gradient-to-r from-sky-400 to-sky-400 blur-sm" />
            </div>

            {/* Animated particles on progress bar */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full blur-sm animate-pulse"
              style={{ left: `${Math.min(progress, 98)}%` }}
            />
          </div>

          {/* Loading text */}
          <div className="flex items-center justify-center">
            <p className="text-slate-400 text-sm font-medium">
              Loading{dots}
            </p>
          </div>
        </div>

        {/* Bottom tagline - positioned properly below loading */}
        <p className="mt-8 text-slate-600 text-xs font-medium tracking-wider uppercase animate-fade-in-up-delay-3">
          Furniture Store Operations Management
        </p>
      </div>

      {/* Inline styles for custom animations */}
      <style>{`
        @keyframes float-slow {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -30px) scale(1.1); }
        }
        @keyframes float-medium {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-20px, 20px) scale(1.05); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.1); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes logo-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes shine {
          0% { transform: translateX(-100%) rotate(45deg); }
          50%, 100% { transform: translateX(100%) rotate(45deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-float-slow { animation: float-slow 8s ease-in-out infinite; }
        .animate-float-medium { animation: float-medium 6s ease-in-out infinite; }
        .animate-pulse-slow { animation: pulse-slow 4s ease-in-out infinite; }
        .animate-spin-slow { animation: spin-slow 3s linear infinite; }
        .animate-logo-float { animation: logo-float 2s ease-in-out infinite; }
        .animate-shine { animation: shine 3s ease-in-out infinite; }
        .animate-shimmer { animation: shimmer 2s ease-in-out infinite; }
        .animate-fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
        .animate-fade-in-up-delay { animation: fade-in-up 0.6s ease-out 0.1s forwards; opacity: 0; }
        .animate-fade-in-up-delay-2 { animation: fade-in-up 0.6s ease-out 0.2s forwards; opacity: 0; }
        .animate-fade-in-up-delay-3 { animation: fade-in-up 0.6s ease-out 0.4s forwards; opacity: 0; }
      `}</style>
    </div>
  );
};

export default MobileLoadingSplash;